/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Link } from '@/components/link';
import SuggestionsList from './suggestions-list';
import SuggestionsGrid from './suggestions-grid';
import { searchUrlBuilder } from '@/lib/url';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import type { PhraseSuggestion, SearchSuggestions } from './types';
import { useEffect, useRef } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { useIsDesktop } from '@/hooks/use-is-desktop';
import { cn } from '@/lib/utils';

interface SuggestionSectionProps {
    searchSuggestions: SearchSuggestions;
    closeAndNavigate: (link: string) => void;
}

interface DidYouMeanProps {
    suggestion: PhraseSuggestion;
    searchPhrase: string;
    onLinkClick: (link: string) => void;
}

const DidYouMean = ({ suggestion, searchPhrase, onLinkClick }: DidYouMeanProps) => {
    const { t } = useTranslation('search');
    const analytics = useAnalytics();

    const handleClickSearchSuggestion = () => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase || '',
            suggestion: suggestion.name,
        });
        onLinkClick(suggestion.link);
    };

    return (
        <div className="mb-3">
            <p className="text-sm text-header-menu-foreground font-medium">
                {t('suggestions.didYouMean')}{' '}
                <Link
                    to={suggestion.link}
                    className="text-header-menu-foreground underline font-semibold text-sm "
                    onMouseDown={handleClickSearchSuggestion}>
                    {suggestion.name}?
                </Link>
            </p>
        </div>
    );
};

export default function SuggestionSection({ searchSuggestions, closeAndNavigate }: SuggestionSectionProps) {
    const { t } = useTranslation('search');
    const analytics = useAnalytics();
    const isDesktop = useIsDesktop();

    const hasCategories = Boolean(searchSuggestions?.categorySuggestions?.length);
    const hasCategoryPhraseSuggestions = Boolean(searchSuggestions?.categoryPhraseSuggestions?.length);
    const hasProducts = Boolean(searchSuggestions?.productSuggestions?.length);
    const hasPhraseSuggestions = Boolean(searchSuggestions?.phraseSuggestions?.length);
    const hasPopularSearches = Boolean(searchSuggestions?.popularSearchSuggestions?.length);

    const firstPhraseSuggestion = searchSuggestions?.phraseSuggestions?.[0];
    const showDidYouMean = hasPhraseSuggestions && firstPhraseSuggestion?.exactMatch === false;
    const searchPhrase = searchSuggestions?.searchPhrase || '';
    const hasLeftSuggestions = showDidYouMean || hasCategoryPhraseSuggestions || hasPopularSearches;
    const viewAllUrl = searchUrlBuilder(searchSuggestions?.searchPhrase || '');

    const lastTrackedSuggestionsRef = useRef<string | null>(null);

    // Track on mount and whenever suggestions changes
    useEffect(() => {
        // Create a unique key based on search phrase and suggestion counts
        const suggestionsKey = `${searchSuggestions?.searchPhrase || ''}-${hasCategories ? (searchSuggestions?.categorySuggestions?.length ?? 0) : 0}-${searchSuggestions?.categoryPhraseSuggestions?.length || 0}-${searchSuggestions?.productSuggestions?.length || 0}-${searchSuggestions?.phraseSuggestions?.length || 0}-${searchSuggestions?.popularSearchSuggestions?.length || 0}`;

        // Only track if we haven't already tracked this specific suggestions combination
        if (suggestionsKey !== lastTrackedSuggestionsRef.current) {
            void analytics.trackViewSearchSuggestions({
                searchInputText: searchSuggestions?.searchPhrase || '',
                suggestions: [
                    ...(searchSuggestions?.categorySuggestions || []).map((s) => s.name),
                    ...(searchSuggestions?.categoryPhraseSuggestions || []).map((s) => s.name),
                    ...(searchSuggestions?.productSuggestions || []).map((s) => s.name),
                    ...(searchSuggestions?.phraseSuggestions || []).map((s) => s.name),
                    ...(searchSuggestions?.popularSearchSuggestions || []).map((s) => s.name),
                ],
            });
            lastTrackedSuggestionsRef.current = suggestionsKey;
        }
    }, [analytics, searchSuggestions, hasCategories]);

    const handleLinkClick = (link: string) => {
        closeAndNavigate(link);
    };

    const handlePhraseSuggestionClick = (suggestion: PhraseSuggestion) => {
        void analytics.trackClickSearchSuggestion({
            searchInputText: searchPhrase,
            suggestion: suggestion.name,
        });
        closeAndNavigate(suggestion.link);
    };

    return (
        <div className="flex gap-6 flex-col lg:flex-row">
            {hasLeftSuggestions && (
                <div className="flex-1">
                    {showDidYouMean && firstPhraseSuggestion && (
                        <DidYouMean
                            suggestion={firstPhraseSuggestion}
                            searchPhrase={searchPhrase}
                            onLinkClick={handleLinkClick}
                        />
                    )}

                    {/* categorySuggestions.suggestedPhrases → categoryPhraseSuggestions */}
                    {hasCategoryPhraseSuggestions && (
                        <div className="mb-0">
                            <div className="text-2xl font-semibold text-card-foreground tracking-wide mb-4 lg:mb-6">
                                {t('suggestions.best')}
                            </div>
                            {isDesktop ? (
                                <>
                                    <SuggestionsList
                                        closeAndNavigate={closeAndNavigate}
                                        suggestions={searchSuggestions.categoryPhraseSuggestions}
                                        searchPhrase={searchSuggestions.searchPhrase}
                                    />
                                    <Link
                                        to={viewAllUrl}
                                        className="mt-1 block text-sm font-semibold text-header-menu-foreground underline"
                                        onMouseDown={() => handleLinkClick(viewAllUrl)}>
                                        {t('suggestions.viewAll')}
                                    </Link>
                                </>
                            ) : (
                                <Carousel
                                    className="w-full"
                                    opts={{ align: 'start', dragFree: true, containScroll: 'trimSnaps' }}
                                    aria-label={t('suggestions.best')}>
                                    <CarouselContent className="ml-0 gap-6">
                                        {(searchSuggestions.categoryPhraseSuggestions ?? []).map((suggestion) => (
                                            <CarouselItem key={suggestion.link} className="basis-auto p-0">
                                                <Link
                                                    to={suggestion.link}
                                                    data-slot="suggestion"
                                                    onMouseDown={() => handlePhraseSuggestionClick(suggestion)}
                                                    className="w-max shrink-0 whitespace-nowrap py-0 text-sm font-medium text-header-menu-foreground transition-colors hover:bg-transparent hover:text-header-menu-foreground/60 focus:bg-transparent focus:text-header-menu-foreground/60">
                                                    {suggestion.name}
                                                </Link>
                                            </CarouselItem>
                                        ))}
                                        <CarouselItem className="basis-auto p-0">
                                            <Link
                                                to={viewAllUrl}
                                                className="w-max shrink-0 whitespace-nowrap text-sm font-semibold text-header-menu-foreground underline transition-colors hover:text-header-menu-foreground/60"
                                                onMouseDown={() => handleLinkClick(viewAllUrl)}>
                                                {t('suggestions.viewAll')}
                                            </Link>
                                        </CarouselItem>
                                    </CarouselContent>
                                </Carousel>
                            )}
                        </div>
                    )}

                    {/* categories suggestions are not needed for now */}
                    {/* {hasCategories && (
                    <div className="mb-0">
                        <div className="text-2xl font-semibold text-card-foreground tracking-wide mb-6">
                            {t('suggestions.best')}
                        </div>
                        <SuggestionsList
                            closeAndNavigate={closeAndNavigate}
                            suggestions={searchSuggestions.categorySuggestions}
                            searchPhrase={searchSuggestions.searchPhrase}
                        />
                    </div>
                )} */}

                    {hasPopularSearches && (
                        <div className="mb-0">
                            <div className="text-2xl font-semibold text-card-foreground tracking-wide mb-6">
                                {t('suggestions.popularSearches')}
                            </div>
                            <SuggestionsList
                                closeAndNavigate={closeAndNavigate}
                                suggestions={searchSuggestions.popularSearchSuggestions}
                                searchPhrase={searchSuggestions.searchPhrase}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className={cn('min-w-0 overflow-hidden', hasLeftSuggestions ? 'flex-[3]' : 'flex-1')}>
                {hasProducts && (
                    <SuggestionsGrid
                        closeAndNavigate={closeAndNavigate}
                        suggestions={searchSuggestions?.productSuggestions}
                        searchPhrase={searchPhrase}
                    />
                )}
            </div>
        </div>
    );
}
